import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Pagination,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line
} from 'recharts';
import { get, syncInternalBugsFromQuery } from '../../services/apiService';
import { isAdmin } from '../../services/authService';
import Loading from '../common/Loading';
import BugsTable from '../releases/BugsTable';

const PAGE_SIZE = 100;

/**
 * Internal Bugs public section: ADO Query + Sync (admin only), monthly trend chart, data table with pagination.
 */
const InternalBugsPublic = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [adoQueryUrl, setAdoQueryUrl] = useState('');
  const [adoQueryLoading, setAdoQueryLoading] = useState(false);
  const [adoQueryError, setAdoQueryError] = useState(null);
  const [syncConfigLoaded, setSyncConfigLoaded] = useState(false);

  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [trendsError, setTrendsError] = useState(null);

  const [priorityFilter, setPriorityFilter] = useState([]);
  const [issueSourceFilter, setIssueSourceFilter] = useState([]);
  const [issueSourceOptions, setIssueSourceOptions] = useState([]);

  const [distribution, setDistribution] = useState({ byPriority: {}, byIssueSource: {} });
  const [distributionLoading, setDistributionLoading] = useState(true);

  const canEditAdoConfig = isAdmin();

  // Load sync config (query URL) on mount – public endpoint
  useEffect(() => {
    get('/api/workitems/internal-bugs/sync-config')
      .then((res) => {
        const data = res?.data ?? res;
        if (data && data.queryUrl) setAdoQueryUrl(data.queryUrl);
      })
      .catch(() => {})
      .finally(() => setSyncConfigLoaded(true));
  }, []);

  // Fetch internal bugs list (public) with pagination and filters
  const fetchBugs = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      const params = { page: pageNum, pageSize: PAGE_SIZE };
      if (priorityFilter.length) params.priority = priorityFilter.join(',');
      if (issueSourceFilter.length) params.issueSource = issueSourceFilter.join(',');
      const response = await get('/api/workitems/internal-bugs/public', params);
      if (response && response.data !== undefined) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        const count = response.count ?? data.length;
        const pages = response.totalPages ?? Math.max(1, Math.ceil(count / PAGE_SIZE));
        setBugs(data);
        setTotalCount(count);
        setTotalPages(pages);
        setError(null);
      } else {
        throw new Error(response?.message || 'Failed to fetch internal bugs');
      }
    } catch (err) {
      console.error('Error fetching internal bugs:', err);
      setError(err.message || 'Failed to load internal bugs.');
      setBugs([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [priorityFilter, issueSourceFilter]);

  useEffect(() => {
    fetchBugs(page);
  }, [page, fetchBugs]);

  // Load distinct issue sources for filter dropdown
  useEffect(() => {
    get('/api/workitems/internal-bugs/distinct?field=issueSource')
      .then((res) => {
        const list = res?.data ?? res;
        setIssueSourceOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => setIssueSourceOptions([]));
  }, []);

  // Fetch monthly trends (public) with same filters
  const fetchTrends = useCallback(async () => {
    try {
      setTrendsLoading(true);
      const params = {};
      if (priorityFilter.length) params.priority = priorityFilter.join(',');
      if (issueSourceFilter.length) params.issueSource = issueSourceFilter.join(',');
      const response = await get('/api/workitems/internal-bugs/trends', params);
      if (response?.success && response.data?.monthlyTrends) {
        setMonthlyTrends(response.data.monthlyTrends);
        setTrendsError(null);
      } else {
        throw new Error(response?.message || 'Failed to load trends');
      }
    } catch (err) {
      console.error('Error fetching internal bug trends:', err);
      setTrendsError(err.message || 'Failed to load monthly trends.');
      setMonthlyTrends([]);
    } finally {
      setTrendsLoading(false);
    }
  }, [priorityFilter, issueSourceFilter]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  // Fetch distribution for pie charts (same filters)
  const fetchDistribution = useCallback(async () => {
    try {
      setDistributionLoading(true);
      const params = {};
      if (priorityFilter.length) params.priority = priorityFilter.join(',');
      if (issueSourceFilter.length) params.issueSource = issueSourceFilter.join(',');
      const response = await get('/api/workitems/internal-bugs/distribution', params);
      if (response?.success && response.data) {
        setDistribution({
          byPriority: response.data.byPriority || {},
          byIssueSource: response.data.byIssueSource || {}
        });
      } else {
        setDistribution({ byPriority: {}, byIssueSource: {} });
      }
    } catch (err) {
      console.error('Error fetching distribution:', err);
      setDistribution({ byPriority: {}, byIssueSource: {} });
    } finally {
      setDistributionLoading(false);
    }
  }, [priorityFilter, issueSourceFilter]);

  useEffect(() => {
    fetchDistribution();
  }, [fetchDistribution]);

  const handleSyncFromQuery = async () => {
    const url = (adoQueryUrl || '').trim();
    if (!url) {
      setAdoQueryError('Please enter an ADO Query URL.');
      return;
    }
    setAdoQueryError(null);
    setAdoQueryLoading(true);
    try {
      await syncInternalBugsFromQuery(url);
      setAdoQueryUrl(url);
      setPage(1);
      await fetchBugs(1);
      await fetchTrends();
      await fetchDistribution();
    } catch (err) {
      console.error('Error syncing internal bugs from query:', err);
      setAdoQueryError(err.message || 'Failed to sync from query.');
    } finally {
      setAdoQueryLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Internal Bugs
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View internal (QA) bugs. Data is stored when an admin runs Sync from Query below. Monthly trend and table use public APIs.
        </Typography>
      </Box>

      {/* ADO Query + Sync – visible to all; edit and Sync only for admin */}
      {syncConfigLoaded && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            ADO Query (Sync from Query)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="ADO Query URL"
              value={adoQueryUrl}
              onChange={(e) => { setAdoQueryUrl(e.target.value); setAdoQueryError(null); }}
              placeholder="https://tr-legal.visualstudio.com/HighQ Tech/_queries/query/{queryId}/"
              fullWidth
              size="small"
              sx={{ flex: '1 1 400px', maxWidth: 640 }}
              helperText={canEditAdoConfig ? 'Paste the URL of your saved ADO query, then click Sync to load results into the internal bugs collection.' : 'Only admins can edit the query URL and run Sync.'}
              disabled={!canEditAdoConfig}
              InputProps={{ readOnly: !canEditAdoConfig }}
            />
            {canEditAdoConfig && (
              <Button
                variant="outlined"
                startIcon={adoQueryLoading ? <CircularProgress size={20} /> : <SyncIcon />}
                onClick={handleSyncFromQuery}
                disabled={adoQueryLoading || !adoQueryUrl.trim()}
                sx={{ minWidth: 120 }}
              >
                {adoQueryLoading ? 'Syncing…' : 'Sync'}
              </Button>
            )}
          </Box>
          {adoQueryError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setAdoQueryError(null)}>
              {adoQueryError}
            </Alert>
          )}
        </Paper>
      )}

      {/* Monthly trend chart with filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Internal Bugs – Charts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="internal-bugs-priority-label">Priority</InputLabel>
            <Select
              labelId="internal-bugs-priority-label"
              id="internal-bugs-priority"
              multiple
              value={priorityFilter}
              label="Priority"
              onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
              renderValue={(selected) => (selected.length === 0 ? 'All' : selected.join(', '))}
            >
              <MenuItem value="P1">P1</MenuItem>
              <MenuItem value="P2">P2</MenuItem>
              <MenuItem value="P3">P3</MenuItem>
              <MenuItem value="P4">P4</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="internal-bugs-issue-source-label">Issue Source</InputLabel>
            <Select
              labelId="internal-bugs-issue-source-label"
              id="internal-bugs-issue-source"
              multiple
              value={issueSourceFilter}
              label="Issue Source"
              onChange={(e) => { setIssueSourceFilter(e.target.value); setPage(1); }}
              renderValue={(selected) => (selected.length === 0 ? 'All' : selected.join(', '))}
            >
              {issueSourceOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Row: 25% Issues by Priority table, 25% Issues by Issue Source table, 50% bar graph */}
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 2, alignItems: 'flex-start', minHeight: 320 }}>
          {/* 1. Issues by Priority (25%) */}
          <Box sx={{ width: '25%', minWidth: 0 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Issues by Priority</Typography>
            {distributionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (() => {
              const byPriority = distribution.byPriority || {};
              const priorityRows = Object.entries(byPriority)
                .filter(([, v]) => v > 0)
                .map(([name, value]) => ({ name, value }));
              if (priorityRows.length === 0) {
                return <Typography color="textSecondary">No data</Typography>;
              }
              return (
                <Table size="small" sx={{ '& td, & th': { borderBottom: 1, borderColor: 'divider' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {priorityRows.map(({ name, value }) => (
                      <TableRow key={name}>
                        <TableCell>{name}</TableCell>
                        <TableCell align="right">{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </Box>
          {/* 2. Issues by Issue Source (25%) */}
          <Box sx={{ width: '25%', minWidth: 0 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Issues by Issue Source</Typography>
            {distributionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (() => {
              const byIssueSource = distribution.byIssueSource || {};
              const sourceRows = Object.entries(byIssueSource)
                .filter(([, v]) => v > 0)
                .map(([name, value]) => ({ name, value }));
              if (sourceRows.length === 0) {
                return <Typography color="textSecondary">No data</Typography>;
              }
              return (
                <Table size="small" sx={{ '& td, & th': { borderBottom: 1, borderColor: 'divider' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Issue Source</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sourceRows.map(({ name, value }) => (
                      <TableRow key={name}>
                        <TableCell>{name}</TableCell>
                        <TableCell align="right">{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </Box>
          {/* 3. Monthly Bug Trends (50%) */}
          <Box sx={{ width: '50%', minWidth: 280 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Monthly Bug Trends: Opened vs Closed</Typography>
            {trendsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                <CircularProgress size={32} />
              </Box>
            ) : trendsError ? (
              <Typography color="error">{trendsError}</Typography>
            ) : !monthlyTrends || monthlyTrends.length === 0 ? (
              <Typography color="textSecondary">No monthly trend data yet. Sync from Query (admin) to populate.</Typography>
            ) : (
              <Box sx={{ height: 280, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={monthlyTrends}
                    margin={{ top: 10, right: 20, left: 0, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" angle={-45} textAnchor="end" height={50} tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" width={35} tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" width={35} tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="created" name="Opened" fill="#d32f2f" />
                    <Bar yAxisId="left" dataKey="closed" name="Closed" fill="#2e7d32" />
                    <Line yAxisId="right" type="monotone" dataKey="backlog" name="Backlog" stroke="#1976d2" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Data table with pagination 100 */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Internal Bugs ({totalCount} total)
        </Typography>
        {loading ? (
          <Loading message="Loading internal bugs..." />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <BugsTable bugs={bugs} bugType="internal" />
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2 }}>
                <Typography variant="body2">
                  Page {page} of {totalPages} ({bugs.length} of {totalCount} bugs)
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default InternalBugsPublic;
